/**
 * The chrome around the tools: buttons, headings, and the four sentences the
 * scanner can say.
 *
 * One file for all five languages rather than five files, unlike the tool copy
 * next door. These are forty short strings that every tool page needs, so
 * splitting them per language would mean five chunks fetched to render one
 * page — the opposite of the trade that makes the copy bundles worth splitting.
 *
 * The two failure sentences are separate on purpose. "No face detected" and
 * "the scanner could not load" are different problems with different fixes, and
 * telling someone their face was not found when in fact our WASM 404'd is the
 * kind of message that gets screenshotted.
 */

import type { ContentLang } from "@fazly/shared/contentLang";

import type { ScannerStrings } from "../../components/tools/FaceScanner";

export interface ToolsUi {
  readonly scanner: ScannerStrings;
  /** Section headings on a tool page. */
  readonly howToRead: string;
  readonly whatItMeans: string;
  readonly faqHeading: string;
  readonly relatedHeading: string;
  /** Above the glossary terms and guides that name this tool (`lib/graph.ts`). */
  readonly explainedHeading: string;
  /** Alt text for the diagram. `%s` is the tool's H1. See `glossary/ui.ts`. */
  readonly diagramLabel: string;
  readonly methodHeading: string;
  readonly methodBody: string;
  readonly weightsHeading: string;
  /** The tools index. */
  readonly indexTitle: string;
  readonly indexDescription: string;
  readonly indexH1: string;
  readonly indexStandfirst: string;
  /** Result chrome. */
  readonly yourResult: string;
  readonly allMeasures: string;
  readonly outOf100: string;
  /** The attractiveness test's scale. See `unit: "ten"` in `tools/types.ts`. */
  readonly outOf10: string;
  /**
   * The attractiveness test's two lists: the measurements scoring highest and
   * lowest in this photo. The honest version of the verdict that query asks
   * for — it names which proportions moved the number, not what kind of face
   * it is.
   */
  readonly helps: string;
  readonly holdsBack: string;
  readonly shape: string;
  readonly confidence: string;
  readonly leftEye: string;
  readonly rightEye: string;
  readonly upperThird: string;
  readonly middleThird: string;
  readonly lowerThird: string;
  readonly gonialAngle: string;
  readonly target: string;
  /**
   * The install CTA under a finished result.
   *
   * Here rather than in the marketing bundle, which already holds the same two
   * button labels: a tool page loads `TOOLS_UI` and nothing else, and pulling
   * `MARKETING` in for two strings would put all five language bundles into the
   * tools chunk to render one button.
   */
  readonly installTitle: string;
  /**
   * The same card on the tools hub, where nothing has been measured yet —
   * `installTitle` says "This measured your geometry", which is only true
   * under a result.
   */
  readonly installTitleHub: string;
  readonly installBody: string;
  readonly installAppStore: string;
  readonly installPlay: string;
  /**
   * "Share my result" under a finished scan — see `ShareResult.tsx`.
   *
   * `shareInvite` is the line that travels: it is drawn on the image and ends
   * the text, so it is the one sentence the person RECEIVING the share reads.
   * `shareComposite` names the looksmax test's number, which has no entry in
   * `METRIC_LABELS` because it is the eight of them weighted together.
   * `shareAttractiveness` names the attractiveness test's reading of that same
   * composite out of ten. It is the test's own name in each language, not
   * "attractiveness" alone: the page says a photo cannot measure attractiveness,
   * so the card must not print the word as if it were the measurement.
   */
  readonly shareButton: string;
  readonly shareCopied: string;
  readonly shareInvite: string;
  readonly shareComposite: string;
  readonly shareAttractiveness: string;
}

const SHARED_UNITS = {
  degrees: "°",
} as const;

export const TOOLS_UI: Record<ContentLang, ToolsUi> = {
  en: {
    scanner: {
      idle: "Drop a front-facing photo",
      hint: "Or choose one from your device. JPG, PNG or WebP. It is measured here and never sent anywhere.",
      choose: "Choose a photo",
      preparing: "Preparing the scanner…",
      retry: "Try another photo",
      again: "Scan another photo",
      noFace:
        "No face found in that photo. A front-facing shot in even light, with the whole face in frame, works best.",
      engineFailed:
        "The scanner could not load. That is on us, not the photo — reload the page, and if it happens again your network may be blocking large files.",
      unreadable:
        "That file couldn't be opened in this browser — it is the format, not your face. Try a JPG or PNG, or take a screenshot of the photo and use that.",
      unreadableHeic:
        "This browser can't open HEIC photos — it is the format, not your face. Take a screenshot of the photo and use that, or set your camera to save JPG.",
      turned:
        "Your head is turned too far for an honest reading — symmetry and the width ratios would be measuring the angle, not your face. Face the camera straight on and try again.",
      tooSmall:
        "The face is too small in that photo to measure accurately. Use one where your face fills more of the frame — a close selfie works best.",
      turnedNote:
        "Your head is turned about %s° in this photo. Symmetry and the width ratios read lower when a face is turned; a square-on photo will give a fairer result.",
      privacy: "Measured on your device. Nothing is uploaded.",
      overlayLabel: "Your photograph with the measured lines drawn on it",
    },
    howToRead: "How to read this",
    whatItMeans: "What this measures",
    faqHeading: "Questions",
    relatedHeading: "Measure something else",
    explainedHeading: "Read more about this measurement",
    diagramLabel: "%s — drawn on a reference face",
    methodHeading: "Method",
    methodBody:
      "Every measurement here comes from 478 facial landmarks detected by Google's MediaPipe face mesh, running as WebAssembly in your browser. The face is levelled by the eye line before anything is measured, and every distance is normalised so the result does not depend on your camera or how close you stood to it.",
    weightsHeading: "How the composite is weighted",
    indexTitle: "Free Face Analysis Tools — Eleven On-Device Tests | Fazly",
    indexDescription:
      "Eleven free face tests that run entirely in your browser: attractiveness, looksmax score, face shape, symmetry, golden ratio, canthal tilt, jawline and more. Nothing is uploaded.",
    indexH1: "Free face analysis tools",
    indexStandfirst:
      "Eleven tests, all on your own device. No account, no upload, no waiting.",
    yourResult: "Your result",
    allMeasures: "Every measurement",
    outOf100: "out of 100",
    outOf10: "out of 10",
    helps: "Working for this photo",
    holdsBack: "Holding it back",
    shape: "Face shape",
    confidence: "Margin over the next shape",
    leftEye: "Left eye",
    rightEye: "Right eye",
    upperThird: "Upper",
    middleThird: "Middle",
    lowerThird: "Lower",
    gonialAngle: "Gonial angle",
    target: "Target",
    installTitle: "This measured your geometry. The app measures the rest.",
    installTitleHub:
      "These tools measure your geometry. The app measures the rest.",
    installBody:
      "Seven scores — skin, eyes, hair and harmony as well as the lines drawn here — then the one area to work on and a daily routine for it. Free to start.",
    installAppStore: "download for iphone",
    installPlay: "download for android",
    shareButton: "share my result",
    shareCopied: "link copied",
    shareInvite: "Measure yours free",
    shareComposite: "Looksmax score",
    shareAttractiveness: "Attractiveness test",
  },

  tr: {
    scanner: {
      idle: "Önden çekilmiş bir fotoğraf bırak",
      hint: "Ya da cihazından seç. JPG, PNG veya WebP. Burada ölçülür, hiçbir yere gönderilmez.",
      choose: "Fotoğraf seç",
      preparing: "Tarayıcı hazırlanıyor…",
      retry: "Başka bir fotoğraf dene",
      again: "Başka bir fotoğraf tara",
      noFace:
        "O fotoğrafta yüz bulunamadı. Yüzün tamamının kadraja girdiği, dengeli ışıkta, önden çekilmiş bir kare en iyisidir.",
      engineFailed:
        "Tarayıcı yüklenemedi. Bu fotoğrafın değil, bizim sorunumuz — sayfayı yenile; yine olursa ağın büyük dosyaları engelliyor olabilir.",
      unreadable:
        "Bu dosya bu tarayıcıda açılamadı — sorun yüzünde değil, dosya biçiminde. JPG ya da PNG dene veya fotoğrafın ekran görüntüsünü alıp onu kullan.",
      unreadableHeic:
        "Bu tarayıcı HEIC fotoğrafları açamıyor — sorun yüzünde değil, dosya biçiminde. Fotoğrafın ekran görüntüsünü alıp onu kullan ya da kamerayı JPG kaydedecek şekilde ayarla.",
      turned:
        "Başın dürüst bir ölçüm için fazla dönük — simetri ve genişlik oranları yüzünü değil açıyı ölçerdi. Kameraya tam karşıdan bak ve tekrar dene.",
      tooSmall:
        "Bu fotoğrafta yüz, doğru ölçülemeyecek kadar küçük. Yüzünün kadrajın daha büyük kısmını kapladığı bir fotoğraf kullan — yakından çekilmiş bir selfie en iyisi.",
      turnedNote:
        "Bu fotoğrafta başın yaklaşık %s° dönük. Yüz dönükken simetri ve genişlik oranları daha düşük çıkar; tam karşıdan çekilmiş bir fotoğraf daha adil bir sonuç verir.",
      privacy: "Cihazında ölçüldü. Hiçbir şey yüklenmedi.",
      overlayLabel: "Ölçülen çizgilerin fotoğrafının üstüne çizilmiş hâli",
    },
    howToRead: "Bu nasıl okunur",
    whatItMeans: "Bu ne ölçüyor",
    faqHeading: "Sorular",
    relatedHeading: "Başka bir şey ölç",
    explainedHeading: "Bu ölçüm hakkında daha fazlası",
    diagramLabel: "%s — referans bir yüz üzerinde çizilmiş",
    methodHeading: "Yöntem",
    methodBody:
      "Buradaki her ölçüm, tarayıcında WebAssembly olarak çalışan Google MediaPipe yüz ağının tespit ettiği 478 yüz noktasından gelir. Herhangi bir şey ölçülmeden önce yüz göz hattına göre yatırılır ve her mesafe normalize edilir; böylece sonuç kameranı ya da ona ne kadar yakın durduğunu yansıtmaz.",
    weightsHeading: "Bileşik puan nasıl ağırlıklandırılıyor",
    indexTitle:
      "Ücretsiz Yüz Analizi Araçları — Cihazında Çalışan On Bir Test | Fazly",
    indexDescription:
      "Tamamı tarayıcında çalışan on bir ücretsiz yüz ölçüm aracı: güzellik testi, looksmax puanı, yüz şekli, simetri, altın oran, canthal tilt, çene hattı, fWHR ve dahası. Hiçbir şey yüklenmez.",
    indexH1: "Ücretsiz yüz analizi araçları",
    indexStandfirst:
      "On bir test, hepsi kendi cihazında. Hesap yok, yükleme yok, bekleme yok.",
    yourResult: "Sonucun",
    allMeasures: "Bütün ölçümler",
    outOf100: "/ 100",
    outOf10: "/ 10",
    helps: "Bu fotoğrafta lehine olanlar",
    holdsBack: "Puanı aşağı çekenler",
    shape: "Yüz şekli",
    confidence: "Sonraki şekle fark",
    leftEye: "Sol göz",
    rightEye: "Sağ göz",
    upperThird: "Üst",
    middleThird: "Orta",
    lowerThird: "Alt",
    gonialAngle: "Gonial açı",
    target: "Hedef",
    installTitle: "Bu, geometriyi ölçtü. Uygulama geri kalanını ölçüyor.",
    installTitleHub:
      "Bu araçlar geometriyi ölçer. Uygulama geri kalanını ölçüyor.",
    installBody:
      "Yedi puan — burada çizilen çizgilerin yanı sıra cilt, gözler, saç ve uyum — sonra üzerinde çalışılacak tek alan ve onun için günlük bir rutin. Başlaması ücretsiz.",
    installAppStore: "iphone için indir",
    installPlay: "android için indir",
    shareButton: "sonucumu paylaş",
    shareCopied: "bağlantı kopyalandı",
    shareInvite: "Seninkini ücretsiz ölç",
    shareComposite: "Looksmax puanı",
    shareAttractiveness: "Yakışıklılık ve güzellik testi",
  },

  de: {
    scanner: {
      idle: "Frontalfoto hier ablegen",
      hint: "Oder eines vom Gerät auswählen. JPG, PNG oder WebP. Es wird hier gemessen und nirgendwohin gesendet.",
      choose: "Foto auswählen",
      preparing: "Scanner wird vorbereitet…",
      retry: "Anderes Foto versuchen",
      again: "Weiteres Foto scannen",
      noFace:
        "Auf diesem Foto wurde kein Gesicht gefunden. Am besten funktioniert eine frontale Aufnahme bei gleichmäßigem Licht, mit dem ganzen Gesicht im Bild.",
      engineFailed:
        "Der Scanner konnte nicht geladen werden. Das liegt an uns, nicht am Foto — lade die Seite neu; passiert es erneut, blockiert dein Netzwerk womöglich große Dateien.",
      unreadable:
        "Diese Datei lässt sich in diesem Browser nicht öffnen — das liegt am Format, nicht an deinem Gesicht. Versuch ein JPG oder PNG, oder mach einen Screenshot des Fotos und nimm den.",
      unreadableHeic:
        "Dieser Browser kann keine HEIC-Fotos öffnen — das liegt am Format, nicht an deinem Gesicht. Mach einen Screenshot des Fotos und nimm den, oder stell die Kamera auf JPG um.",
      turned:
        "Dein Kopf ist für eine ehrliche Messung zu weit gedreht — Symmetrie und Breitenverhältnisse würden den Winkel messen, nicht dein Gesicht. Schau direkt in die Kamera und versuch es noch einmal.",
      tooSmall:
        "Das Gesicht ist auf diesem Foto zu klein, um es genau zu messen. Nimm eines, auf dem dein Gesicht mehr vom Bild ausfüllt — ein nahes Selfie funktioniert am besten.",
      turnedNote:
        "Dein Kopf ist auf diesem Foto um etwa %s° gedreht. Bei gedrehtem Kopf fallen Symmetrie und Breitenverhältnisse niedriger aus; ein Foto von vorn ergibt ein faireres Ergebnis.",
      privacy: "Auf deinem Gerät gemessen. Nichts wird hochgeladen.",
      overlayLabel: "Dein Foto mit den eingezeichneten Messlinien",
    },
    howToRead: "So liest du das",
    whatItMeans: "Was hier gemessen wird",
    faqHeading: "Fragen",
    relatedHeading: "Etwas anderes messen",
    explainedHeading: "Mehr über diese Messung",
    diagramLabel: "%s — auf einem Referenzgesicht eingezeichnet",
    methodHeading: "Methode",
    methodBody:
      "Jede Messung hier stammt aus 478 Gesichtspunkten, die Googles MediaPipe-Gesichtsnetz als WebAssembly in deinem Browser erkennt. Vor jeder Messung wird das Gesicht an der Augenlinie ausgerichtet, und jede Strecke wird normiert — das Ergebnis hängt also weder von deiner Kamera noch davon ab, wie nah du standest.",
    weightsHeading: "Wie der Gesamtwert gewichtet ist",
    indexTitle:
      "Kostenlose Gesichtsanalyse-Tools — Elf Tests im Browser | Fazly",
    indexDescription:
      "Elf kostenlose Werkzeuge zur Gesichtsvermessung, komplett im Browser: Attraktivitätstest, Looksmax-Score, Gesichtsform, Symmetrie, Goldener Schnitt, Canthal Tilt, Kieferlinie, fWHR und mehr. Nichts wird hochgeladen.",
    indexH1: "Kostenlose Gesichtsanalyse-Tools",
    indexStandfirst:
      "Elf Tests, alle auf deinem eigenen Gerät. Kein Konto, kein Upload, kein Warten.",
    yourResult: "Dein Ergebnis",
    allMeasures: "Alle Messungen",
    outOf100: "von 100",
    outOf10: "von 10",
    helps: "Was in diesem Foto hilft",
    holdsBack: "Was den Wert drückt",
    shape: "Gesichtsform",
    confidence: "Abstand zur nächsten Form",
    leftEye: "Linkes Auge",
    rightEye: "Rechtes Auge",
    upperThird: "Oben",
    middleThird: "Mitte",
    lowerThird: "Unten",
    gonialAngle: "Gonialwinkel",
    target: "Ziel",
    installTitle: "Das war die Geometrie. Die App misst den Rest.",
    installTitleHub:
      "Diese Tools messen die Geometrie. Die App misst den Rest.",
    installBody:
      "Sieben Werte — neben den hier gezogenen Linien auch Haut, Augen, Haare und Harmonie — dann der eine Bereich, an dem du arbeitest, und eine tägliche Routine dafür. Kostenlos starten.",
    installAppStore: "für iPhone laden",
    installPlay: "für Android laden",
    shareButton: "Ergebnis teilen",
    shareCopied: "Link kopiert",
    shareInvite: "Miss deins kostenlos",
    shareComposite: "Looksmax-Score",
    shareAttractiveness: "Attraktivitätstest",
  },

  es: {
    scanner: {
      idle: "Suelta una foto de frente",
      hint: "O elige una de tu dispositivo. JPG, PNG o WebP. Se mide aquí y no se envía a ninguna parte.",
      choose: "Elegir una foto",
      preparing: "Preparando el escáner…",
      retry: "Probar otra foto",
      again: "Escanear otra foto",
      noFace:
        "No se encontró ninguna cara en esa foto. Lo que mejor funciona es una toma de frente, con luz uniforme y la cara entera en el encuadre.",
      engineFailed:
        "El escáner no pudo cargarse. Es cosa nuestra, no de la foto: recarga la página, y si vuelve a pasar puede que tu red esté bloqueando archivos grandes.",
      unreadable:
        "Este navegador no ha podido abrir ese archivo — el problema es el formato, no tu cara. Prueba con un JPG o PNG, o haz una captura de pantalla de la foto y usa esa.",
      unreadableHeic:
        "Este navegador no abre fotos HEIC — el problema es el formato, no tu cara. Haz una captura de pantalla de la foto y usa esa, o configura la cámara para guardar en JPG.",
      turned:
        "Tienes la cabeza demasiado girada para una lectura honesta — la simetría y las proporciones de anchura medirían el ángulo, no tu cara. Mira a la cámara de frente y vuelve a intentarlo.",
      tooSmall:
        "La cara sale demasiado pequeña en esa foto para medirla con precisión. Usa una en la que tu cara ocupe más del encuadre — un selfie de cerca funciona mejor.",
      turnedNote:
        "En esta foto tienes la cabeza girada unos %s°. Con la cara girada, la simetría y las proporciones de anchura salen más bajas; una foto de frente da un resultado más justo.",
      privacy: "Medido en tu dispositivo. No se sube nada.",
      overlayLabel: "Tu fotografía con las líneas medidas dibujadas encima",
    },
    howToRead: "Cómo leer esto",
    whatItMeans: "Qué mide",
    faqHeading: "Preguntas",
    relatedHeading: "Medir otra cosa",
    explainedHeading: "Más sobre esta medición",
    diagramLabel: "%s — dibujado sobre una cara de referencia",
    methodHeading: "Método",
    methodBody:
      "Cada medida viene de 478 puntos faciales detectados por la malla facial MediaPipe de Google, ejecutada como WebAssembly en tu navegador. La cara se nivela por la línea de los ojos antes de medir nada, y cada distancia se normaliza para que el resultado no dependa de tu cámara ni de lo cerca que estuvieras.",
    weightsHeading: "Cómo se pondera la puntuación compuesta",
    indexTitle: "Herramientas Gratis de Análisis Facial — Once Tests | Fazly",
    indexDescription:
      "Once herramientas gratuitas de medición facial que funcionan por completo en tu navegador: test de atractivo, puntuación looksmax, forma de cara, simetría, proporción áurea, canthal tilt, mandíbula, fWHR y más. No se sube nada.",
    indexH1: "Herramientas gratis de análisis facial",
    indexStandfirst:
      "Once tests, todos en tu propio dispositivo. Sin cuenta, sin subir nada, sin esperas.",
    yourResult: "Tu resultado",
    allMeasures: "Todas las medidas",
    outOf100: "sobre 100",
    outOf10: "sobre 10",
    helps: "Lo que suma en esta foto",
    holdsBack: "Lo que resta",
    shape: "Forma de cara",
    confidence: "Margen sobre la siguiente forma",
    leftEye: "Ojo izquierdo",
    rightEye: "Ojo derecho",
    upperThird: "Superior",
    middleThird: "Medio",
    lowerThird: "Inferior",
    gonialAngle: "Ángulo gonial",
    target: "Objetivo",
    installTitle: "Esto midió la geometría. La app mide el resto.",
    installTitleHub:
      "Estas herramientas miden la geometría. La app mide el resto.",
    installBody:
      "Siete puntuaciones — piel, ojos, pelo y armonía además de las líneas trazadas aquí — luego la única área en la que trabajar y una rutina diaria para ella. Empezar es gratis.",
    installAppStore: "descargar para iphone",
    installPlay: "descargar para android",
    shareButton: "compartir mi resultado",
    shareCopied: "enlace copiado",
    shareInvite: "Mide el tuyo gratis",
    shareComposite: "Puntuación looksmax",
    shareAttractiveness: "Test de atractivo",
  },

  pt: {
    scanner: {
      idle: "Solte uma foto de frente",
      hint: "Ou escolha uma do seu aparelho. JPG, PNG ou WebP. É medida aqui e não é enviada a lugar nenhum.",
      choose: "Escolher uma foto",
      preparing: "Preparando o scanner…",
      retry: "Tentar outra foto",
      again: "Escanear outra foto",
      noFace:
        "Nenhum rosto foi encontrado nessa foto. O que funciona melhor é uma foto de frente, com luz uniforme e o rosto inteiro no enquadramento.",
      engineFailed:
        "O scanner não conseguiu carregar. O problema é nosso, não da foto — recarregue a página; se acontecer de novo, sua rede pode estar bloqueando arquivos grandes.",
      unreadable:
        "Não foi possível abrir esse arquivo neste navegador — o problema é o formato, não o seu rosto. Tente um JPG ou PNG, ou tire um print da foto e use o print.",
      unreadableHeic:
        "Este navegador não abre fotos HEIC — o problema é o formato, não o seu rosto. Tire um print da foto e use o print, ou configure a câmera para salvar em JPG.",
      turned:
        "Sua cabeça está virada demais para uma leitura honesta — a simetria e as proporções de largura mediriam o ângulo, não o seu rosto. Olhe de frente para a câmera e tente de novo.",
      tooSmall:
        "O rosto está pequeno demais nessa foto para medir com precisão. Use uma em que o rosto ocupe mais do quadro — uma selfie de perto funciona melhor.",
      turnedNote:
        "Sua cabeça está virada cerca de %s° nesta foto. Com o rosto virado, a simetria e as proporções de largura ficam mais baixas; uma foto de frente dá um resultado mais justo.",
      privacy: "Medido no seu aparelho. Nada é enviado.",
      overlayLabel: "Sua foto com as linhas medidas desenhadas em cima",
    },
    howToRead: "Como ler isto",
    whatItMeans: "O que isto mede",
    faqHeading: "Perguntas",
    relatedHeading: "Medir outra coisa",
    explainedHeading: "Mais sobre esta medição",
    diagramLabel: "%s — desenhado sobre um rosto de referência",
    methodHeading: "Método",
    methodBody:
      "Cada medida vem de 478 pontos faciais detectados pela malha facial MediaPipe do Google, rodando como WebAssembly no seu navegador. O rosto é nivelado pela linha dos olhos antes de qualquer medição, e cada distância é normalizada para que o resultado não dependa da sua câmera nem de quão perto você estava.",
    weightsHeading: "Como a pontuação composta é ponderada",
    indexTitle: "Ferramentas Grátis de Análise Facial — Onze Testes | Fazly",
    indexDescription:
      "Onze ferramentas gratuitas de medição facial que rodam inteiramente no seu navegador: teste de beleza, pontuação looksmax, formato de rosto, simetria, proporção áurea, canthal tilt, mandíbula, fWHR e mais. Nada é enviado.",
    indexH1: "Ferramentas grátis de análise facial",
    indexStandfirst:
      "Onze testes, todos no seu próprio aparelho. Sem conta, sem envio, sem espera.",
    yourResult: "Seu resultado",
    allMeasures: "Todas as medidas",
    outOf100: "de 100",
    outOf10: "de 10",
    helps: "O que ajuda nesta foto",
    holdsBack: "O que puxa para baixo",
    shape: "Formato do rosto",
    confidence: "Margem sobre o próximo formato",
    leftEye: "Olho esquerdo",
    rightEye: "Olho direito",
    upperThird: "Superior",
    middleThird: "Médio",
    lowerThird: "Inferior",
    gonialAngle: "Ângulo gonial",
    target: "Alvo",
    installTitle: "Isto mediu a geometria. O app mede o resto.",
    installTitleHub: "Estas ferramentas medem a geometria. O app mede o resto.",
    installBody:
      "Sete notas — pele, olhos, cabelo e harmonia além das linhas traçadas aqui — depois a única área para trabalhar e uma rotina diária para ela. Grátis para começar.",
    installAppStore: "baixar para iphone",
    installPlay: "baixar para android",
    shareButton: "compartilhar meu resultado",
    shareCopied: "link copiado",
    shareInvite: "Meça o seu grátis",
    shareComposite: "Pontuação looksmax",
    shareAttractiveness: "Teste de beleza",
  },
};

/** The six face shapes, named in each language. */
export const SHAPE_NAMES: Record<ContentLang, Record<string, string>> = {
  en: {
    oval: "Oval",
    round: "Round",
    square: "Square",
    heart: "Heart",
    oblong: "Oblong",
    diamond: "Diamond",
  },
  tr: {
    oval: "Oval",
    round: "Yuvarlak",
    square: "Kare",
    heart: "Kalp",
    oblong: "Uzun",
    diamond: "Elmas",
  },
  de: {
    oval: "Oval",
    round: "Rund",
    square: "Eckig",
    heart: "Herzförmig",
    oblong: "Länglich",
    diamond: "Rautenförmig",
  },
  es: {
    oval: "Ovalada",
    round: "Redonda",
    square: "Cuadrada",
    heart: "Corazón",
    oblong: "Alargada",
    diamond: "Diamante",
  },
  pt: {
    oval: "Oval",
    round: "Redondo",
    square: "Quadrado",
    heart: "Coração",
    oblong: "Alongado",
    diamond: "Diamante",
  },
};

/**
 * Format a measured value for display.
 *
 * Degrees carry an explicit sign, because on canthal tilt the sign IS the
 * result: "+6°" and "6°" read the same to a skim, and the whole reason someone
 * opened that page was to find out which side of zero they are on.
 */
export function formatValue(
  value: number,
  unit: "score" | "ten" | "ratio" | "degrees" | "percent" | "shape",
): string {
  switch (unit) {
    case "ten":
      return value.toFixed(1);
    case "degrees":
      return `${value > 0 ? "+" : ""}${value.toFixed(1)}${SHARED_UNITS.degrees}`;
    case "percent":
      return `${value.toFixed(1)}%`;
    case "ratio":
      return value.toFixed(2);
    default:
      return String(Math.round(value));
  }
}
